import type {
  BusinessOperationCloseRequest,
  BusinessOperationCompletionResult,
  RuntimePrimaryNavigationItem,
} from '@jukun/platform-sdk/auth';
import type {
  WorkflowPageFailure,
  WorkflowPageReadyReport,
  WorkflowWorkItemBridge,
} from '@jukun/platform-sdk/workflow';

export const NAVIGATION_RUNTIME_CACHE_LIMIT = 2;
export const NAVIGATION_RUNTIME_PREFIX = 'PORTAL_NAV::';

export type NavigationRuntimeState =
  | 'starting'
  | 'active'
  | 'parked'
  | 'destroying';

export type NavigationRuntimeCallbacks<TToken, TFeedback, TBridge> = {
  refreshAccessToken: () => Promise<TToken>;
  registerPrimaryNavigation: (
    items: readonly RuntimePrimaryNavigationItem[],
  ) => void;
  navigate: (path: string) => void;
  onPrimaryNavigationChange: (navigationCode: string) => void;
  onSessionExpired: () => void;
  reportOperationFeedback: (feedback: TFeedback) => void;
  reportOperationRouteReady?: (pageRoute: string) => void;
  reportOperationRouteFailed?: (failure: unknown) => void;
  registerOperationPageBridge?: (bridge?: TBridge) => void;
  reportOperationCompleted?: (
    result: BusinessOperationCompletionResult,
  ) => void;
  requestOperationClose?: (request: BusinessOperationCloseRequest) => void;
  reportWorkflowPageReady?: (report: WorkflowPageReadyReport) => void;
  reportWorkflowPageFailed?: (failure: WorkflowPageFailure) => void;
  registerWorkflowPageBridge?: (bridge?: WorkflowWorkItemBridge) => void;
};

export type StableNavigationRuntimeCallbacks<TToken, TFeedback, TBridge> =
  NavigationRuntimeCallbacks<TToken, TFeedback, TBridge>;

type NavigationRuntimeEntry<TToken, TFeedback, TBridge> = {
  applicationCode: string;
  runtimeName: string;
  entryFingerprint: string;
  sessionEpoch: string;
  state: NavigationRuntimeState;
  generation: number;
  lastActivated: number;
  navigationSnapshot: readonly RuntimePrimaryNavigationItem[];
  lease?: {
    generation: number;
    callbacks: NavigationRuntimeCallbacks<TToken, TFeedback, TBridge>;
  };
  stableCallbacks: StableNavigationRuntimeCallbacks<TToken, TFeedback, TBridge>;
};

export type ActivateNavigationRuntimeArgs<TToken, TFeedback, TBridge> = {
  applicationCode: string;
  entryFingerprint: string;
  sessionEpoch: string;
  callbacks: NavigationRuntimeCallbacks<TToken, TFeedback, TBridge>;
  start: (context: {
    runtimeName: string;
    callbacks: StableNavigationRuntimeCallbacks<TToken, TFeedback, TBridge>;
  }) => Promise<void>;
  destroy: (runtimeName: string) => Promise<boolean>;
};

export type NavigationRuntimeActivation = {
  runtimeName: string;
  release: () => Promise<void>;
};

function inactiveLeaseError() {
  return new DOMException('导航运行时租约已失效', 'AbortError');
}

export class NavigationRuntimeRegistry<TToken, TFeedback, TBridge> {
  private readonly entries = new Map<
    string,
    NavigationRuntimeEntry<TToken, TFeedback, TBridge>
  >();
  private readonly queues = new Map<string, Promise<void>>();
  private activationSequence = 0;

  async activate(
    args: ActivateNavigationRuntimeArgs<TToken, TFeedback, TBridge>,
  ): Promise<NavigationRuntimeActivation> {
    await this.invalidateChangedRuntime(args);
    await this.evictParkedFor(args.applicationCode, args.destroy);

    return this.enqueue(args.applicationCode, async () => {
      let entry = this.entries.get(args.applicationCode);
      if (!entry) {
        entry = this.createEntry(args);
        this.entries.set(args.applicationCode, entry);
      }
      const generation = entry.generation + 1;
      entry.generation = generation;
      entry.state = 'starting';
      entry.lastActivated = ++this.activationSequence;
      entry.lease = { generation, callbacks: args.callbacks };
      try {
        await args.start({
          runtimeName: entry.runtimeName,
          callbacks: entry.stableCallbacks,
        });
      } catch (error) {
        if (entry.generation === generation) entry.lease = undefined;
        await this.destroyEntry(entry, args.destroy);
        throw error;
      }
      if (this.entries.get(args.applicationCode) !== entry
        || entry.generation !== generation) {
        throw inactiveLeaseError();
      }
      entry.state = 'active';
      if (entry.navigationSnapshot.length) {
        args.callbacks.registerPrimaryNavigation(entry.navigationSnapshot);
      }
      return {
        runtimeName: entry.runtimeName,
        release: () => this.park(args.applicationCode, generation, args.destroy),
      };
    });
  }

  async invalidate(
    applicationCode: string,
    destroy: (runtimeName: string) => Promise<boolean>,
  ) {
    const entry = this.entries.get(applicationCode);
    if (!entry) return true;
    this.detachEntry(entry);
    return this.enqueue(applicationCode, async () => {
      if (this.entries.get(applicationCode) !== entry) return true;
      return this.destroyEntry(entry, destroy);
    });
  }

  async invalidateAll(destroy: (runtimeName: string) => Promise<boolean>) {
    const applicationCodes = [...this.entries.keys()];
    const results = await Promise.all(applicationCodes.map(applicationCode =>
      this.invalidate(applicationCode, destroy)));
    return results.every(Boolean);
  }

  getNavigationSnapshot(applicationCode: string) {
    return this.entries.get(applicationCode)?.navigationSnapshot ?? [];
  }

  getState(applicationCode: string) {
    return this.entries.get(applicationCode)?.state;
  }

  resetForTest() {
    this.entries.clear();
    this.queues.clear();
    this.activationSequence = 0;
  }

  private createEntry(
    args: ActivateNavigationRuntimeArgs<TToken, TFeedback, TBridge>,
  ): NavigationRuntimeEntry<TToken, TFeedback, TBridge> {
    const entry: NavigationRuntimeEntry<TToken, TFeedback, TBridge> = {
      applicationCode: args.applicationCode,
      runtimeName: `${NAVIGATION_RUNTIME_PREFIX}${args.applicationCode}`,
      entryFingerprint: args.entryFingerprint,
      sessionEpoch: args.sessionEpoch,
      state: 'starting',
      generation: 0,
      lastActivated: 0,
      navigationSnapshot: [] as readonly RuntimePrimaryNavigationItem[],
      lease: undefined,
      stableCallbacks: undefined as unknown as
        StableNavigationRuntimeCallbacks<TToken, TFeedback, TBridge>,
    };
    const currentLease = () => entry.state === 'active'
      || entry.state === 'starting' ? entry.lease : undefined;
    const forward = <TArgs extends unknown[]>(
      select: (callbacks: NavigationRuntimeCallbacks<TToken, TFeedback, TBridge>) =>
        ((...values: TArgs) => void) | undefined,
    ) => (...values: TArgs) => {
      const lease = currentLease();
      if (lease) select(lease.callbacks)?.(...values);
    };
    entry.stableCallbacks = {
      refreshAccessToken: async () => {
        const lease = currentLease();
        if (!lease) throw inactiveLeaseError();
        const result = await lease.callbacks.refreshAccessToken();
        if (currentLease() !== lease) throw inactiveLeaseError();
        return result;
      },
      registerPrimaryNavigation: items => {
        const lease = currentLease();
        if (!lease) return;
        entry.navigationSnapshot = [...items];
        lease.callbacks.registerPrimaryNavigation(items);
      },
      navigate: forward(callbacks => callbacks.navigate),
      onPrimaryNavigationChange: forward(
        callbacks => callbacks.onPrimaryNavigationChange,
      ),
      onSessionExpired: forward(callbacks => callbacks.onSessionExpired),
      reportOperationFeedback: forward(
        callbacks => callbacks.reportOperationFeedback,
      ),
      reportOperationRouteReady: forward(
        callbacks => callbacks.reportOperationRouteReady,
      ),
      reportOperationRouteFailed: forward(
        callbacks => callbacks.reportOperationRouteFailed,
      ),
      registerOperationPageBridge: forward(
        callbacks => callbacks.registerOperationPageBridge,
      ),
      reportOperationCompleted: forward(
        callbacks => callbacks.reportOperationCompleted,
      ),
      requestOperationClose: forward(callbacks => callbacks.requestOperationClose),
      reportWorkflowPageReady: forward(
        callbacks => callbacks.reportWorkflowPageReady,
      ),
      reportWorkflowPageFailed: forward(
        callbacks => callbacks.reportWorkflowPageFailed,
      ),
      registerWorkflowPageBridge: forward(
        callbacks => callbacks.registerWorkflowPageBridge,
      ),
    };
    return entry;
  }

  private async invalidateChangedRuntime(
    args: ActivateNavigationRuntimeArgs<TToken, TFeedback, TBridge>,
  ) {
    const existing = this.entries.get(args.applicationCode);
    if (existing && (existing.entryFingerprint !== args.entryFingerprint
      || existing.sessionEpoch !== args.sessionEpoch)) {
      await this.invalidate(args.applicationCode, args.destroy);
    }
  }

  private async park(
    applicationCode: string,
    generation: number,
    destroy: (runtimeName: string) => Promise<boolean>,
  ) {
    await this.enqueue(applicationCode, async () => {
      const entry = this.entries.get(applicationCode);
      if (!entry || entry.generation !== generation) return;
      entry.lease = undefined;
      entry.state = 'parked';
    });
    await this.evictExcessParked(destroy);
  }

  private async evictParkedFor(
    applicationCode: string,
    destroy: (runtimeName: string) => Promise<boolean>,
  ) {
    const current = this.entries.get(applicationCode);
    if (current?.state === 'destroying') {
      throw new Error(`导航运行时 ${applicationCode} 回收未完成，已停止继续启动`);
    }
    if (current) return;
    if ([...this.entries.values()].some(entry => entry.state === 'destroying')) {
      throw new Error('存在未完成回收的导航运行时，已停止继续启动');
    }
    if (this.entries.size < NAVIGATION_RUNTIME_CACHE_LIMIT) return;
    const candidate = this.oldestParkedEntry();
    if (candidate) {
      const destroyed = await this.invalidate(candidate.applicationCode, destroy);
      if (!destroyed) {
        throw new Error(
          `导航运行时 ${candidate.applicationCode} 回收失败，已停止继续启动`,
        );
      }
    }
  }

  private async evictExcessParked(
    destroy: (runtimeName: string) => Promise<boolean>,
  ) {
    while (this.entries.size > NAVIGATION_RUNTIME_CACHE_LIMIT) {
      const candidate = this.oldestParkedEntry();
      if (!candidate) return;
      const destroyed = await this.invalidate(candidate.applicationCode, destroy);
      if (!destroyed) return;
    }
  }

  private oldestParkedEntry() {
    return [...this.entries.values()]
      .filter(entry => entry.state === 'parked')
      .sort((left, right) => left.lastActivated - right.lastActivated)[0];
  }

  private async destroyEntry(
    entry: NavigationRuntimeEntry<TToken, TFeedback, TBridge>,
    destroy: (runtimeName: string) => Promise<boolean>,
  ) {
    this.detachEntry(entry);
    const destroyed = await destroy(entry.runtimeName);
    if (destroyed && this.entries.get(entry.applicationCode) === entry) {
      this.entries.delete(entry.applicationCode);
    }
    return destroyed;
  }

  private detachEntry(
    entry: NavigationRuntimeEntry<TToken, TFeedback, TBridge>,
  ) {
    entry.lease = undefined;
    entry.navigationSnapshot = [];
    if (entry.state === 'destroying') return;
    entry.state = 'destroying';
    entry.generation += 1;
  }

  private enqueue<T>(applicationCode: string, operation: () => Promise<T>) {
    const previous = this.queues.get(applicationCode) ?? Promise.resolve();
    const run = previous.catch(() => undefined).then(operation);
    const tail = run.then(() => undefined, () => undefined);
    this.queues.set(applicationCode, tail);
    return run.finally(() => {
      if (this.queues.get(applicationCode) === tail) {
        this.queues.delete(applicationCode);
      }
    });
  }
}
